import {
  Box,
  Grid,
  FormControlLabel,
  Switch,
  Button,
  Stack,
  Chip,
  Paper,
  Typography,
  ListItem,
  IconButton,
  ListItemText,
  List,
} from "@mui/material";
import VirtualizedAutoCompleteField from "../../VirtualizedAutoCompleteField";
import { useEffect, useState, useMemo } from "react";
import TextInput from "../../TextInput";
import isEqual from "lodash.isequal";
import EtimDataType from "../../EtimDataType";
import { DataType, PageSessionKey } from "../../../../types/constant";
import { usePreparationData } from "../../../../contexts/hooks/usePreparationData";
import { useSearch } from "../../../../contexts/hooks/useReusableHook";
import { fetchValues } from "../../../../services/valueService";
import { fetchUnits } from "../../../../services/unitService";
import DeleteForeverRoundedIcon from "@mui/icons-material/DeleteForeverRounded";
import AddIncludedFeatureAutoComplete from "./AddIncludedFeatureAutoComplete";
import { usePreload } from "../../../../contexts/DataContext/PreloadContext";
import { useData } from "../../../../contexts/DataContext/DataContextt";
import TabulatedGridCollapsibleV2 from "../../TabulatedGridCollapsibleV2";
import { RowActions } from "../../../../utils/RowActions";

export default function IncludedFeaturesForm({ props, onSave }) {
  const { confirmDialog } = usePreload();

  const { valueOptions, unitOptions } = usePreparationData({
    sources: ["values", "units"],
  });
  const {
    options: valueLocalOptions,
    search: searchValues,
    searching: isValueSearching,
  } = useSearch(valueOptions, fetchValues, {
    displayNameFields: ["valueDisplayName", "valueName"],
    extraKeys: [
      "valueCode",
      "valueName",
      "valueDisplayName",
      "valueTooltip",
      "valueCodeOld",
      "valueCodeAlternative",
      "valueImage",
    ],
  });

  const {
    options: unitLocalOptions,
    search: searchUnits,
    searching: isUnitSearching,
  } = useSearch(unitOptions, fetchUnits, {
    displayNameFields: ["unitDisplayName", "unitName"],
    extraKeys: [],
  });

  const cancelShow = props?.showCancel ?? true;
  const saveText = props?.saveText ?? "Save";

  const defaultIncludedFeature = useMemo(() => {
    const arrayValues = props.row.includedFeatureValues?.split("|") ?? [];
    const orderedValues =
      props.row.temporaryValues ??
      props.row.values
        .filter((v) => arrayValues.includes(v.valueCode)) // optional: keep only matching
        .sort(
          (a, b) =>
            arrayValues.indexOf(a.valueCode) - arrayValues.indexOf(b.valueCode),
        );

    const temporaryValues = orderedValues.map((m) => ({
      ...m,
      displayName:
        `${m.valueCode} | ` +
        `${m.valueDisplayName || m.valueName || "no display"}`,
    }));

    return {
      includedFeatureClassCode: props.row.includedFeatureClassCode ?? "",
      includedFeatureFeatureId: props.row.includedFeatureFeatureId ?? "",
      includedFeatureIndexOrder: props.row.includedFeatureIndexOrder ?? 0,
      //includedFeatureIsActive: props.row.includedFeatureIsActive ?? true,
      includedFeatureIsDefault: props.row.includedFeatureIsDefault ?? false,
      includedFeatureIsNew: props.row.includedFeatureIsNew ?? true,
      includedFeatureType:
        props.row.featureType ?? props.row.includedFeatureType ?? "",
      includedFeatureUniqueId: props.row.includedFeatureUniqueId ?? "",
      includedFeatureIsSelectableAttribute:
        props.row.includedFeatureIsSelectableAttribute ?? false,
      isSelectionNew: props.row.isSelectionNew ?? false,

      // always store as STRING internally
      includedFeatureUnitCodeTemporary: props.row.includedFeatureUnitCode ?? "",
      includedFeatureValuesTemporary: props.row.includedFeatureValues ?? "",
      temporaryValues: temporaryValues,
      temporaryUnit: props.row.unit,
    };
  }, [props?.row]);

  const [currentIncludedFeature, setCurrentIncludedFeature] = useState(
    defaultIncludedFeature,
  );
  const [currentIncludedFeatureOriginal, setCurrentIncludedFeatureOriginal] =
    useState(defaultIncludedFeature);

  const handleReorder = (sorted) => {
    setCurrentIncludedFeature((prev) => ({
      ...prev,
      temporaryValues: sorted,
      includedFeatureValuesTemporary: sorted.map((m) => m.id)?.join("|"),
    }));
  };

  // sync when row changes
  useEffect(() => {
    setCurrentIncludedFeature(defaultIncludedFeature);
    setCurrentIncludedFeatureOriginal(defaultIncludedFeature);
  }, [defaultIncludedFeature]);

  const hasChanges = !isEqual(
    currentIncludedFeature,
    currentIncludedFeatureOriginal,
  );

  const enableValues =
    currentIncludedFeature.includedFeatureType === DataType.ALPHANUMERIC;
  const enableUnit =
    currentIncludedFeature.includedFeatureType === DataType.NUMERIC ||
    currentIncludedFeature.includedFeatureType === DataType.RANGE ||
    currentIncludedFeature.includedFeatureType === DataType.STRING;

  const handleSave = async () => {
    await onSave(currentIncludedFeature, props.row);

    if (!props.isUpdate) {
      setCurrentIncludedFeature(defaultIncludedFeature);
      setCurrentIncludedFeatureOriginal(defaultIncludedFeature);
    } else {
      setCurrentIncludedFeatureOriginal(currentIncludedFeature);
    }
  };

  const handleRemoveValue = async (row) => {
    const { id, isSelectionNew } = row;

    if (!isSelectionNew) {
      const ok = await confirmDialog.show({
        title: "Delete Value",
        message: `Are you sure you want to delete this value? ${id}`,
      });
      if (!ok) {
        confirmDialog.close();
        return;
      }
    }

    let stringValues =
      currentIncludedFeature.includedFeatureValuesTemporary.split("|");

    let temporaryValues = currentIncludedFeature.temporaryValues;

    stringValues = stringValues.filter((f) => f !== id);
    temporaryValues = temporaryValues.filter((f) => f.id !== id);

    setCurrentIncludedFeature((prev) => ({
      ...prev,
      includedFeatureValuesTemporary: stringValues.join("|"),
      temporaryValues: temporaryValues,
    }));

    confirmDialog.close();
  };

  const valueHeaders = [
    {
      prop: "",
      text: " ",
      isIndex: true,
      width: 10,
    },
    {
      prop: "valueCode",
      text: "Code",
      width: 10,
      withIsNewStatus: true,
      link: {
        path: `/value/edit`,
        action: (row) => {
          sessionStorage.setItem(
            PageSessionKey.EDIT_PAGEVALUE,
            JSON.stringify(row),
          );
          window.open(`/value/edit/${row.valueCode}`, "_blank");
        },
      },
    },
    {
      prop: "valueCodeAlternative",
      text: "Alternative Code",
      width: 150,
      isImage: false,
    },
    { prop: "valueCodeOld", text: "Old Code", width: 150, isImage: false },
    { prop: "valueDisplayName", text: "Name", isImage: false },
    { prop: "valueImage", text: "Symbols", width: 10, isImage: true },
  ];

  const pageRowActions = [
    {
      tooltip: "Delete Value",
      icon: <DeleteForeverRoundedIcon />,
      onClick: (row) => {
        handleRemoveValue(row);
      },
    },
  ];
  return (
    <>
      <Grid container sx={{ mt: 2 }} spacing={1}>
        <Grid size={{ xs: 12, sm: 3 }}>
          <TextInput
            label="Index Order"
            type="number"
            value={currentIncludedFeature.includedFeatureIndexOrder}
            onChange={(e) =>
              setCurrentIncludedFeature((prev) => ({
                ...prev,
                includedFeatureIndexOrder: e.target.value,
              }))
            }
          />
        </Grid>

        <Grid size={{ xs: 12, sm: 3 }}>
          <EtimDataType
            label="Feature Type"
            value={currentIncludedFeature.includedFeatureType}
            disabled
          />
        </Grid>

        <Box mt={2} ml={3}>
          {/*
          <FormControlLabel
            control={
              <Switch
                size="small"
                checked={currentIncludedFeature.includedFeatureIsActive}
                onChange={(e) =>
                  setCurrentIncludedFeature((prev) => ({
                    ...prev,
                    includedFeatureIsActive: e.target.checked,
                  }))
                }
              />
            }
            label="Is Active"
          />
          */}
          <FormControlLabel
            control={
              <Switch
                size="small"
                checked={currentIncludedFeature.includedFeatureIsDefault}
                onChange={(e) =>
                  setCurrentIncludedFeature((prev) => ({
                    ...prev,
                    includedFeatureIsDefault: e.target.checked,
                  }))
                }
              />
            }
            label="Is Default"
          />

          <FormControlLabel
            control={
              <Switch
                size="small"
                checked={currentIncludedFeature.includedFeatureIsNew}
                onChange={(e) =>
                  setCurrentIncludedFeature((prev) => ({
                    ...prev,
                    includedFeatureIsNew: e.target.checked,
                  }))
                }
              />
            }
            label="Is New"
          />
        </Box>
      </Grid>

      {/* UNITS */}
      {enableUnit && (
        <Box mt={3}>
          <VirtualizedAutoCompleteField
            options={unitLocalOptions}
            limit={5}
            label="Unit Codes"
            value={
              currentIncludedFeature.includedFeatureUnitCodeTemporary
                ?.split("|")
                .filter(Boolean) ?? []
            }
            onChange={(val) =>
              setCurrentIncludedFeature((prev) => ({
                ...prev,
                includedFeatureUnitCodeTemporary: (Array.isArray(val)
                  ? val
                  : [val]
                ).join("|"),
              }))
            }
            searching={isUnitSearching}
            onSearch={searchUnits}
          />
        </Box>
      )}

      {/* VALUES */}
      {
        enableValues && (
          <Box>
            <Box mt={4} display={"flex"}>
              <Typography variant="h5" gutterBottom sx={{ display: "block" }}>
                Values ({currentIncludedFeature.temporaryValues?.length})
              </Typography>
              <Box sx={{ ml: "auto" }}>
                <AddIncludedFeatureAutoComplete
                  label="Add Values"
                  options={valueLocalOptions}
                  onChange={(value) => {}}
                  defaultValue={currentIncludedFeature.temporaryValues?.map(
                    (m) => ({
                      ...m,
                      isSelectionNew: m.isSelectionNew ?? false,
                      displayName: m.displayName,
                    }),
                  )}
                  onSearch={searchValues}
                  searching={isValueSearching}
                  callBack={(state) => {
                    const { id, displayName } = state.item;
                    let stringValues =
                      currentIncludedFeature.includedFeatureValuesTemporary.split(
                        "|",
                      );

                    let temporaryValues =
                      currentIncludedFeature.temporaryValues;

                    if (state.action === "add") {
                      stringValues = [...stringValues, id];
                      temporaryValues = [
                        ...temporaryValues,
                        {
                          ...state.item,
                          isSelectionNew: true,
                        },
                      ];
                      setCurrentIncludedFeature((prev) => ({
                        ...prev,
                        includedFeatureValuesTemporary: stringValues.join("|"),
                        temporaryValues: temporaryValues,
                      }));
                    }
                  }}
                />
              </Box>
            </Box>
            <TabulatedGridCollapsibleV2
              headers={valueHeaders}
              data={currentIncludedFeature.temporaryValues}
              isDraggable={true}
              onReorder={handleReorder}
              rowActions={(row) => (
                <RowActions row={row} actions={pageRowActions} />
              )}
            />
          </Box>
        )
        /*(
        <Box mt={3}>
          <VirtualizedAutoCompleteField
            options={valueLocalOptions}
            limit={3}
            label="Value Codes"
            value={
              currentIncludedFeature.includedFeatureValuesTemporary
                ?.split("|")
                .filter(Boolean) ?? []
            }
            onChange={(val) =>
              setCurrentIncludedFeature((prev) => ({
                ...prev,
                includedFeatureValuesTemporary: (Array.isArray(val)
                  ? val
                  : [val]
                ).join("|"),
              }))
            }
            searching={isValueSearching}
            onSearch={searchValues}
          />
        </Box>
      )*/
      }

      <Box mt={2} display="flex" justifyContent="flex-end">
        {cancelShow && <Button onClick={props.onCancel}>Cancel</Button>}

        <Button
          variant="contained"
          disabled={!hasChanges}
          loading={props.uploading}
          onClick={handleSave}
        >
          {saveText}
        </Button>
      </Box>
    </>
  );
}
